import { type CancellationProtection } from "@clipboard-health/contract-backend-main";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { Button } from "@src/appV2/redesign/components/Button";
import { ShiftBottomSheetBookabilityCriteriaCard } from "@src/appV2/redesign/Shift/BottomSheet/BookabilityCriteriaCard";
import { useModalState } from "@src/appV2/redesign/system/ui-components";
import { isRebrandingEnabled } from "@src/appV2/redesign/system/ui-theme";

import { CancellationProtectionTermsBottomSheet } from "./CancellationProtectionTermsBottomSheet";
import { SegmentedText } from "./SegmentedText";
import { getCancellationProtectionPolicyDescription } from "./utils/getCancellationProtectionDescription";

interface ShiftCancellationProtectionCardProps {
  formattedPayAmount?: string;
  cancellationProtection: CancellationProtection;
  workplaceName: string;
  workplaceId?: string;
}

export function ShiftCancellationProtectionCard(props: ShiftCancellationProtectionCardProps) {
  const { formattedPayAmount, cancellationProtection, workplaceName, workplaceId } = props;
  const termsModalState = useModalState();

  useLogEffect(APP_V2_USER_EVENTS.CANCELLATION_PROTECTION_SHIFT_CARD_VIEWED, {
    cancellationProtection,
    workplaceId,
  });

  if (!cancellationProtection.enabled) {
    return (
      <ShiftBottomSheetBookabilityCriteriaCard
        intent="limited"
        title="No Cancellation Protection"
        description="You will not receive any earnings from the workplace if they cancel your shift or send you home early."
        illustrationType={isRebrandingEnabled() ? "parachute-gift" : "parachute-orange"}
      />
    );
  }

  const { cancellationProtectionWindowInHours, isSentHomeProtected } = cancellationProtection;

  const descriptionSegments = getCancellationProtectionPolicyDescription(
    formattedPayAmount,
    cancellationProtectionWindowInHours,
    isSentHomeProtected
  );

  return (
    <>
      <ShiftBottomSheetBookabilityCriteriaCard
        title="Cancellation Protection"
        description={
          <SegmentedText
            segments={descriptionSegments}
            sx={{ color: (theme) => theme.palette.text.secondary }}
          />
        }
        illustrationType={isRebrandingEnabled() ? "parachute-gift" : "parachute-blue"}
        footer={
          <Button
            fullWidth
            variant="outlined"
            size="medium"
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.CANCELLATION_PROTECTION_SHIFT_CARD_VIEW_TERMS_CLICKED, {
                workplaceId,
                cancellationProtection,
              });
              termsModalState.openModal();
            }}
          >
            View terms
          </Button>
        }
      />
      <CancellationProtectionTermsBottomSheet
        modalState={termsModalState}
        cancellationProtectionWindowInHours={cancellationProtectionWindowInHours}
        isSentHomeProtected={isSentHomeProtected}
        workplaceName={workplaceName}
        workplaceId={workplaceId}
      />
    </>
  );
}
